import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Receipt, Plus, CalendarDays, Briefcase } from "lucide-react";
import { emptyLine, emptyPayout, validateExpense, appendExpense, PayoutPicker, ExpenseLines } from "../expenses/ExpenseFields";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const eur = (n) => `€${Number(n || 0).toFixed(2)}`;
const STATUSES = [["all", "All"], ["submitted", "Submitted"], ["approved", "Approved"], ["paid", "Paid"], ["rejected", "Rejected"]];
const draftKey = (id) => `mm_expense_draft_${id || "me"}`;
export const readDraft = (id) => { try { return JSON.parse(localStorage.getItem(draftKey(id))); } catch { return null; } };
const hasContent = (d) => d && (d.lines || []).some(l => l.description || l.amount || l.invoice_number);

const BADGE = { submitted: "bg-amber-100 text-amber-700", approved: "bg-blue-100 text-blue-700", paid: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-700" };

function NewClaim({ cfg, onDone, ownWalletId }) {
  const draft = readDraft(ownWalletId);
  const [kind, setKind] = useState(draft?.kind || "general");
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(draft?.eventId || "");
  const [categories, setCategories] = useState([]);
  const [payout, setPayout] = useState({ ...emptyPayout(), ...(draft?.payout || {}) });
  const [lines, setLines] = useState(draft?.lines?.length ? draft.lines.map(l => ({ ...emptyLine(), ...l, file: null })) : [emptyLine()]);
  const hadFiles = (draft?.lines || []).some(l => l.file_name);

  // Auto-save the draft (bank/PayPal details and receipts are never stored in the browser).
  useEffect(() => {
    const d = { kind, eventId, payout: { method: payout.method, useSaved: payout.useSaved, wallet_id: payout.wallet_id },
      lines: lines.map(({ file, ...l }) => ({ ...l, file_name: file?.name || l.file_name || "" })), saved_at: new Date().toISOString() };
    if (hasContent(d)) localStorage.setItem(draftKey(ownWalletId), JSON.stringify(d));
  }, [kind, eventId, payout, lines, ownWalletId]);
  const discard = () => { localStorage.removeItem(draftKey(ownWalletId)); onDone(false); };
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    axios.get(`${API}/member/payout-details`, cfg).then(r => setSaved(r.data)).catch(() => {});
    axios.get(`${API}/member/expenses/events`, cfg).then(r => setEvents(r.data)).catch(() => {});
    axios.get(`${API}/categories/public`).then(r => setCategories(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (kind === "event" && !eventId) return toast.error("Choose the event");
    const err = validateExpense(payout, lines, saved);
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      if (kind === "event") fd.append("event_id", eventId);
      appendExpense(fd, payout, lines, saved);
      const r = await axios.post(`${API}/member/expenses`, fd, cfg);
      localStorage.removeItem(draftKey(ownWalletId));
      toast.success(r.data.message);
      onDone(true);
    } catch (e2) { toast.error(e2.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl p-4 shadow-sm space-y-4" data-testid="member-expense-form">
      {hasContent(draft) && (
        <div className="flex items-start gap-2 text-xs bg-amber-50 text-amber-800 rounded-xl p-3" data-testid="expense-draft-restored">
          <span className="flex-1">Draft restored from {new Date(draft.saved_at).toLocaleString()}.{hadFiles ? " Please re-attach your receipts." : ""}{payout.method !== "wallet" && !payout.useSaved ? " Re-enter bank/PayPal details (not stored in drafts)." : ""}</span>
          <button type="button" onClick={discard} className="font-semibold underline shrink-0" data-testid="expense-draft-discard">Discard</button>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        {[["general", "General expense", Briefcase], ["event", "Event expense", CalendarDays]].map(([k, label, Ico]) => (
          <button key={k} type="button" onClick={() => setKind(k)} data-testid={`expense-kind-${k}`}
            className={`py-3 rounded-xl border-2 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${kind === k ? "border-[#1e3a5f] bg-[#1e3a5f]/5 text-[#1e3a5f]" : "border-slate-200 text-slate-500"}`}>
            <Ico className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>
      {kind === "event" && (events.length ? (
        <select value={eventId} onChange={e => setEventId(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border text-sm bg-white" data-testid="expense-event-select">
          <option value="">Choose event…</option>
          {events.map(ev => <option key={ev.id} value={ev.id}>{`${ev.name}${ev.start_date ? ` · ${ev.start_date}` : ""}`}</option>)}
        </select>
      ) : <p className="text-sm text-slate-500 bg-slate-50 rounded-xl p-3" data-testid="expense-no-events">No events are open for expense claims right now.</p>)}
      <PayoutPicker payout={payout} setPayout={setPayout} ownWalletId={ownWalletId} saved={saved} />
      <ExpenseLines lines={lines} setLines={setLines} categories={categories} />
      <button type="submit" disabled={busy || (kind === "event" && !events.length)} className="w-full py-3 rounded-xl bg-[#1e3a5f] text-white font-bold disabled:opacity-50" data-testid="member-expense-submit">
        {busy ? "Submitting…" : `Submit ${lines.length} line${lines.length === 1 ? "" : "s"}`}
      </button>
    </form>
  );
}

function ClaimRow({ x }) {
  return (
    <div className="p-3 flex items-start gap-3" data-testid={`member-expense-${x.id}`}>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm text-[#1e3a5f] truncate">{x.description}</p>
        <p className="text-xs text-slate-500 truncate">{x.kind === "event" ? x.event_name : "General expense"} · {(x.created_at || "").slice(0, 10)}{x.category ? ` · ${x.category}` : ""}</p>
        {x.status === "rejected" && x.rejection_reason && <p className="text-xs text-red-600 mt-0.5" data-testid={`member-expense-reason-${x.id}`}>Reason: {x.rejection_reason}</p>}
      </div>
      <div className="text-right shrink-0">
        <p className="font-bold text-sm">{eur(x.amount)}</p>
        <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full capitalize ${BADGE[x.status] || "bg-slate-100 text-slate-500"}`}>{x.status}</span>
      </div>
    </div>
  );
}

export default function MemberExpenses({ token, ownWalletId }) {
  const cfg = { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
  const [view, setView] = useState("list");
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("all");
  const load = useCallback(() => {
    axios.get(`${API}/member/expenses`, cfg).then(r => setItems(r.data)).catch(() => setItems([]));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const count = (s) => (items || []).filter(x => s === "all" || x.status === s).length;
  const shown = (items || []).filter(x => status === "all" || x.status === status);
  return (
    <div className="space-y-4" data-testid="member-expenses">
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-[#1e3a5f] flex items-center gap-2"><Receipt className="w-5 h-5" /> My Expenses</h2>
        <button onClick={() => setView(view === "new" ? "list" : "new")} data-testid="member-expense-new-btn"
          className="px-4 py-2 rounded-full bg-[#1e3a5f] text-white text-sm font-semibold flex items-center gap-1">
          {view === "new" ? "Save draft & close" : <><Plus className="w-4 h-4" /> {hasContent(readDraft(ownWalletId)) ? "Continue claim" : "New claim"}</>}
        </button>
      </div>
      {view === "new" ? <NewClaim cfg={cfg} ownWalletId={ownWalletId} onDone={(submitted) => { setView("list"); if (submitted) load(); }} /> : (
        <>
          {hasContent(readDraft(ownWalletId)) && (
            <button onClick={() => setView("new")} className="w-full text-left bg-amber-50 border border-amber-200 rounded-2xl p-3 text-sm text-amber-800 flex items-center justify-between" data-testid="expense-draft-banner">
              <span>You have an unfinished expense claim</span><span className="font-semibold underline">Continue</span>
            </button>
          )}
          <div className="flex flex-wrap gap-1.5" data-testid="member-expense-status-tabs">
            {STATUSES.map(([k, label]) => (
              <button key={k} onClick={() => setStatus(k)} data-testid={`member-expense-status-${k}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border transition-colors ${status === k ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white text-slate-600"}`}>
                {label} ({count(k)})
              </button>
            ))}
          </div>
          <div className="bg-white rounded-2xl shadow-sm divide-y divide-slate-100">
            {shown.map(x => <ClaimRow key={x.id} x={x} />)}
            {items && !shown.length && <p className="p-8 text-center text-sm text-slate-400" data-testid="member-expense-empty">No expenses here yet.</p>}
            {!items && <p className="p-8 text-center text-sm text-slate-400">Loading…</p>}
          </div>
        </>
      )}
    </div>
  );
}
